import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { type AdminUserSummary, useDeleteUser } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useUser } from "@/lib/user-context";

interface UserDeleteDialogProps {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserDeleteDialog({ user, open, onOpenChange }: UserDeleteDialogProps) {
  const { user: currentUser } = useUser();
  const deleteUser = useDeleteUser();
  const [error, setError] = useState("");
  const deleting = deleteUser.isPending;

  const isSelf = currentUser?.email === user?.email;

  const handleDelete = async () => {
    if (!user || isSelf) {
      return;
    }
    setError("");

    try {
      await deleteUser.mutateAsync({ userId: user.id });
      onOpenChange(false);
    } catch (err) {
      logger.error("[user-delete] failed:", err);
      setError("Failed to delete user. Please try again.");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete User</AlertDialogTitle>
          <AlertDialogDescription>
            {isSelf ? (
              "You cannot delete your own account."
            ) : (
              <>
                Are you sure you want to delete <strong>{user?.full_name || user?.email}</strong>?
                This action cannot be undone. All of their data, including activity logs, will be
                permanently removed.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          {!isSelf && (
            <AlertDialogAction variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete User"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
